import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";

import { AppointmentExchangeResponse } from "@/api";
import { useEndFollowingStart } from "@/common/components/appointments/shared/hooks/useEndFollowingStart";
import { useCounselors } from "@/common/components/counselor-select/hooks/useCounselors";
import { resolveExchangeCounselorLabel } from "@/pages/exchange/Exchange.utils";
import { useExchangeOfferMutations } from "@/pages/exchange/hooks/useExchangeOfferMutations";
import { useExchangeableAppointmentSelection } from "@/pages/exchange/hooks/useExchangeableAppointmentSelection";
import { useTranslation } from "@/translations";

import { MakeOfferFormValues } from "../MakeOfferDialog.types";
import { currentWeekRange, filterByRequestedPurposes, makeOfferValidationSchema } from "../MakeOfferDialog.utils";

interface Params {
  exchange: AppointmentExchangeResponse;
  onClose: () => void;
}

/**
 * Form state of the "Angebot abgeben" dialog. The date range and the purpose filter only narrow the
 * list of own Termine; the selected Termin and the note are what gets submitted.
 */
export function useMakeOffer({ exchange, onClose }: Params) {
  const { t } = useTranslation();
  const { counselors } = useCounselors();
  const { makeOffer } = useExchangeOfferMutations();

  const validationSchema = useMemo(() => makeOfferValidationSchema(t), [t]);

  const formik = useFormikWithDirtyForm<MakeOfferFormValues>({
    initialValues: { ...currentWeekRange(), onlyRequestedPurposes: true, appointmentId: null, note: "" },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (values.appointmentId === null) {
        return;
      }
      const note = values.note.trim();
      try {
        await makeOffer.mutateAsync(
          {
            exchangeId: exchange.id,
            request: { offeredAppointmentId: values.appointmentId, note: note === "" ? null : note },
          },
          { onSuccess: onClose }
        );
      } catch {
        // Reported by the global error handling; the dialog stays open so the input is kept.
      }
    },
  });

  const selection = useExchangeableAppointmentSelection(formik);
  const { onlyRequestedPurposes, appointmentId } = formik.values;

  const visibleAppointments = useMemo(
    () => filterByRequestedPurposes(selection.appointments, exchange.requestedPurposeKeys, onlyRequestedPurposes),
    [selection.appointments, exchange.requestedPurposeKeys, onlyRequestedPurposes]
  );

  // The end date (Datum-Bis) moves along with the start date (Datum-Von) and keeps the range's width:
  // someone looking for a counter-offer browses a range, so shifting the start shifts the whole week
  // instead of collapsing it to one day as in a new request.
  const moveTillWithFrom = useEndFollowingStart(formik, "from", "till", { emptyEndDurationMinutes: 0 });
  const handleFromChange = (from: Date | null) => {
    moveTillWithFrom(from);
    selection.clearAppointment();
  };

  // Switching the filter on can hide the selected Termin; a hidden selection must not be submittable.
  const handleFilterChange = (checked: boolean) => {
    if (!checked || appointmentId === null) {
      return;
    }
    const selected = selection.appointments.find((appointment) => appointment.appointmentId === appointmentId);
    if (selected && !exchange.requestedPurposeKeys.includes(selected.purposeKey)) {
      selection.clearAppointment();
    }
  };

  return {
    formik,
    counselorLabel: resolveExchangeCounselorLabel(exchange, counselors),
    ...selection,
    appointments: visibleAppointments,
    handleFromChange,
    handleFilterChange,
    submitDisabled: appointmentId === null || makeOffer.isPending,
  };
}
